import { useRef, useState } from "react"

import { PresenceAndCrowdDetectPopover } from "src/components/Homes/HomeDetails/PresenceAndCrowdDetection/PresenceAndCrowdDetectPopover"
import { PresenceAndOccupancyIcon } from "src/components/Homes/HomeDetails/PresenceAndCrowdDetection/PresenceAndOccupancyIcon"
import { useFeatureAvailability } from "src/data/featureAvailability/logic/useFeatureAvailability"
import { THome, Violations } from "src/data/homes/types/homeTypes"
import { usePatchOccupancy } from "src/data/occupancy/queries/occupancyQueries"
import { IOccupancy } from "src/data/occupancy/types/occupancyTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePatchHomeMotionAutomation } from "src/data/organizations/queries/homeQueries"
import { langKeys } from "src/i18n/langKeys"
import { TTranslateFunction, useTranslate } from "src/i18n/useTranslate"
import { MButton } from "src/ui/Button/MButton"
import {
  MonitoringButton,
  OnOffStatusLabel,
} from "src/ui/MonitoringButton/MonitoringButton"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

/**
 * PresenceAndCrowdDetectButton
 * Combined button component that handles both motion automation (presence detection)
 * and occupancy detection (crowd detection) functionality.
 * Shows the status of either feature and allows toggling through a popover.
 * @example <PresenceAndCrowdDetectButton homeData={home} violations={violations} />
 */
export function PresenceAndCrowdDetectButton({
  motionAutomation,
  violations,
  graphicalButton,
  hideStatus,
  toggleAllowed,
  homeData,
}: {
  motionAutomation: THome["motion_automation"] | undefined
  violations?: Violations[]
  graphicalButton?: boolean
  hideStatus?: boolean
  toggleAllowed?: boolean
  homeData: THome
}) {
  const { org } = useOrganization()
  const { t } = useTranslate()

  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Determine if either feature is enabled for tooltip
  const isMotionAutomationEnabled = motionAutomation?.mode === "enabled"
  const isOccupancyEnabled = homeData.occupancy?.active === true

  const mTooltip = toggleAllowed
    ? getTooltipText({
        motionAutomationEnabled: isMotionAutomationEnabled,
        occupancyActive: isOccupancyEnabled,
        occupancyStatus: homeData.occupancy?.status,
        t,
      })
    : t(langKeys.not_enough_permissions)

  const patchOccupancy = usePatchOccupancy({
    orgId: org?.id,
    homeId: homeData.home_id,
  })

  const patchHomeMotionAutomation = usePatchHomeMotionAutomation({
    orgId: org?.id || "",
  })
  const { available: motionAutomationAvailable } = useFeatureAvailability({
    feature: "motion_automation",
  })

  function disablePresenceIfAvailable() {
    if (!motionAutomationAvailable) {
      return
    }
    patchHomeMotionAutomation.mutate({
      path: { home_id: homeData.home_id },
      body: { mode: "disabled" },
    })
  }

  function updateOccupancy({
    active,
    capacity,
  }: {
    active?: boolean
    capacity?: number
  }) {
    if (active === undefined && capacity !== undefined) {
      patchOccupancy.mutate({ capacity })
      return
    }
    patchOccupancy.mutate({ active, capacity })
  }

  async function update({
    active,
    capacity,
  }: {
    active?: boolean
    capacity?: number
  }) {
    setOpen(false)

    // No active provided: only update capacity if present
    if (active === undefined) {
      updateOccupancy({ capacity })
      return
    }

    // Turning ON should only affect occupancy; presence is enabled via switch
    if (active) {
      updateOccupancy({ active, capacity })
      return
    }

    // Turning OFF: disable presence (if available) and occupancy
    disablePresenceIfAvailable()
    updateOccupancy({ active, capacity })
  }

  // Determine the primary state for display (motion automation takes precedence)
  const primaryState =
    isMotionAutomationEnabled || isOccupancyEnabled ? "enabled" : "disabled"

  const mButton = graphicalButton ? (
    <PresenceAndCrowdDetectButtonIcon
      state={primaryState}
      isUpdating={
        patchOccupancy.isLoading || patchHomeMotionAutomation.isLoading
      }
      disabled={!toggleAllowed}
      onClick={() => setOpen(true)}
      hideStatus={hideStatus}
      homeData={homeData}
    />
  ) : (
    <MButton
      variant="subtle"
      size="small"
      onClick={() => setOpen(true)}
      loading={patchOccupancy.isLoading || patchHomeMotionAutomation.isLoading}
      disabled={!toggleAllowed}
    >
      {t(langKeys.manage)}
    </MButton>
  )

  return (
    <>
      <MTooltip title={mTooltip} placement="bottom">
        <div ref={ref}>{mButton}</div>
      </MTooltip>
      {open && (
        <PresenceAndCrowdDetectPopover
          open={open}
          activeInitial={isOccupancyEnabled}
          capacityInitial={homeData.occupancy?.capacity}
          threshold={homeData.occupancy?.scanned_devices_threshold}
          anchorEl={ref.current}
          handleClose={() => setOpen(false)}
          violations={violations}
          update={update}
          homeData={homeData}
          state={isMotionAutomationEnabled ? "enabled" : "disabled"}
        />
      )}
    </>
  )
}

function PresenceAndCrowdDetectButtonIcon({
  state = "disabled",
  isUpdating,
  disabled,
  onClick,
  hideStatus,
  homeData,
}: {
  state: "enabled" | "disabled" | undefined
  isUpdating?: boolean
  disabled: boolean
  onClick: () => void
  hideStatus?: boolean
  homeData: THome
}) {
  const enabled = state === "enabled"

  return (
    <MonitoringButton
      label={<OnOffStatusLabel isOn={enabled} />}
      labelType={enabled ? "good" : "default"}
      onClick={onClick}
      updating={isUpdating}
      icon={
        <PresenceAndOccupancyIcon
          occupancyActive={homeData.occupancy?.active}
          occupancyStatus={homeData.occupancy?.status}
          motionAutomationState={
            homeData.motion_automation?.mode === "enabled"
              ? "enabled"
              : "disabled"
          }
        />
      }
      hideStatus={hideStatus}
      disabled={disabled}
      row={true}
    />
  )
}

function getTooltipText({
  motionAutomationEnabled,
  occupancyActive,
  occupancyStatus,
  t,
}: {
  motionAutomationEnabled: boolean
  occupancyActive: boolean
  occupancyStatus: IOccupancy["status"] | undefined
  t: TTranslateFunction
}) {
  // Priority: Show crowd detection status if active and detected
  if (occupancyActive && occupancyStatus === "crowd_detected") {
    return t(langKeys.occupancy_status_ongoing)
  }

  if (motionAutomationEnabled && occupancyActive) {
    return t(langKeys.motion_and_occupancy_detection_status_on)
  }

  if (occupancyActive) {
    return t(langKeys.occupancy_status_on)
  }

  if (motionAutomationEnabled) {
    return t(langKeys.motion_detection_status_on)
  }

  // Both disabled
  return t(langKeys.motion_and_occupancy_detection_status_off)
}
